import React, { useCallback, useEffect, useMemo } from 'react';
import { styled } from '@compiled/react';
import debounce from 'lodash/debounce';
import differenceBy from 'lodash/differenceBy';
import flow from 'lodash/flow';
import noop from 'lodash/noop';
import { CustomThemeButton } from '@atlaskit/button';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import AddIcon from '@atlaskit/icon/core/migration/add';
import AddonIcon from '@atlaskit/icon/core/migration/app--addon';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/migration/show-more-horizontal--more';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import AkToolTip from '@atlaskit/tooltip';
import withFireUiAnalytics from '@atlassian/jira-analytics-web-react/src/components/with-fire-ui-analytics.tsx';
import type { AnalyticsEvent } from '@atlassian/jira-common-analytics-v2-wrapped-components/src/types.tsx';

import AsyncIcon from '@atlassian/jira-common-components-async-icon/src/view.tsx';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import type { AddonFilter, EventData } from '@atlassian/jira-connect-utils/src/common/types.tsx';
import broadcastConnectEvent from '@atlassian/jira-connect-utils/src/common/utils/broadcast-connect-event.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { fireUiTriggerSelectedEvent } from '@atlassian/jira-forge-ui-analytics/src/services/issue-panel/index.tsx';
import {
	isKeyMatchPrefix,
	toConnectKey,
	toForgeKey,
} from '@atlassian/jira-forge-ui-utils/src/utils/connect/index.tsx';
import { defineMessages } from '@atlassian/jira-intl';
import { useIntlV2 as useIntl } from '@atlassian/jira-intl/src/v2/use-intl.tsx';
import type { EcosystemContentPanel } from '@atlassian/jira-issue-gira-transformer-types/src/common/types/ecosystem.tsx';
import { FORGE_ENTITY_TYPE } from '@atlassian/jira-issue-view-common-constants/src/ecosystem-constants.tsx';
import type { Panel } from '@atlassian/jira-issue-view-common-types/src/forge-types.tsx';
import { removeTimestamp } from '@atlassian/jira-issue-view-common-utils/src/ecosystem/module-key-helper.tsx';
import { safeComponent } from '@atlassian/jira-issue-view-common-utils/src/safe-component/index.tsx';
import {
	contentPanelAnalyticsData,
	contentPanelAnalyticsOnLoad,
} from '@atlassian/jira-issue-view-common/src/ecosystem/ecosystem-analytics.tsx';
import { connect } from '@atlassian/jira-issue-view-react-redux/src/index.tsx';
import { showCreateWebLinkForm } from '@atlassian/jira-issue-view-store/src/actions/web-links-actions.tsx';
import {
	analyticsSourceSelector,
	baseUrlSelector,
} from '@atlassian/jira-issue-view-store/src/common/state/selectors/context-selector.tsx';
import { ISSUE_QUICK_ADD_CLICKED } from '@atlassian/jira-issue-view-store/src/ecosystem/ecosystem-event.tsx';
import {
	canInstallAddonsSelector,
	ecosystemAllPanelsSelector,
	issueContentPanelsSelector,
} from '@atlassian/jira-issue-view-store/src/ecosystem/ecosystem-extensions-selector.tsx';
import { forgeUserCanSaveIssuePanelSelector } from '@atlassian/jira-issue-view-store/src/ecosystem/forge/forge-extensions-selector.tsx';
import {
	AnalyticsEventToProps,
	fireUIAnalytics,
	useAnalyticsEvents,
	type Attributes,
} from '@atlassian/jira-product-analytics-bridge';
import type { ItemViewType, Props } from './types.tsx';

const SHOW_EXTENSION_COUNT_THRESHOLD = 3;

export const ECOSYSTEM_CONTENT_BUTTON_BASE_DATA_TEST_ID =
	'issue.views.issue-base.foundation.quick-add.ecosystem-content-button';
const iconSize = gridSize * 3;

// FIXME: JIV-15565 this rule has been disabled in order to preserve Ids during extraction

const messages = defineMessages({
	quickAddAddonLabel: {
		id: 'issue.quickadd.addon',
		defaultMessage: 'Add app',
		description: '',
	},
	addMore: {
		id: 'issue.quickadd.addon.add.more',
		defaultMessage: 'Add apps',
		description: '',
	},
});

// @ts-expect-error - TS7006 - Parameter 'extension' implicitly has an 'any' type.
const isNewExtension = (extension) => !!extension.tooltip && !!extension.iconUrl;

export const DropdownItemWithAnalytics = AnalyticsEventToProps('dropdownItem', {
	onClick: 'clicked',
})(DropdownItem);

export const UnsafeAddonsQuickAdd = ({
	isQuickActionsListView = false,
	canInstallAddons = false,
	canSaveForgeIssuePanel = true,
	availableAddons = [],
	contentPanelsInIssue = [],
	// @ts-expect-error - TS2322 - Type '(...args: any[]) => void' is not assignable to type '(obj: PanelListAnalytics) => null | undefined'.
	onContentPanelListLoadedAnalytics = noop,
	// @ts-expect-error - TS2322 - Type '(...args: any[]) => void' is not assignable to type '(data: AppKeyModuleKeyType) => null | undefined'.
	onContentPanelAddAnalytics = noop,
	// @ts-expect-error - TS2322 - Type '(...args: any[]) => void' is not assignable to type '(data: AppKeyModuleKeyType) => null | undefined'.
	onContentPanelFocusAnalytics = noop,
	// @ts-expect-error - TS2322 - Type '(...args: any[]) => void' is not assignable to type '(eventData: EventData) => null | undefined'.
	onAddExtensionsClick = noop,
	onQuickAddClick = noop,
	startIndex,
	createItemView,
	source,
	onSelectContentPanel,
	onAddExtension,
	baseUrl,
	// @ts-expect-error - TS2322 - Type '(...args: any[]) => void' is not assignable to type '(eventData: EventData) => null | undefined'.
	onAddExtensionsDropdownTriggerClick = noop,
}: Props) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const filteredAvailableAddons = useMemo(
		() =>
			availableAddons.filter(
				(addon) =>
					!(
						addon.moduleKey.includes('linked-goals-content-panel') &&
						expVal('native_goals_on_issue_view', 'isGoalsFieldEnabled', false)
					),
			),
		[availableAddons],
	);

	useEffect(() => {
		if (filteredAvailableAddons.length > 0) {
			onContentPanelListLoadedAnalytics(
				contentPanelAnalyticsOnLoad(filteredAvailableAddons, contentPanelsInIssue),
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const getFallbackIcon = (props: { name: string }) => (
		<AddonIcon LEGACY_size="medium" label={props.name} />
	);

	const getIcon = useCallback(
		(extension: EcosystemContentPanel) =>
			extension.iconUrl ? (
				<AsyncIcon
					title={extension.name}
					url={extension.iconUrl}
					width={iconSize}
					height={iconSize}
					fallback={() => getFallbackIcon({ name: extension.name })}
				/>
			) : (
				getFallbackIcon({ name: extension.name })
			),
		[],
	);

	const getExtensionsToShowIconsFor = useCallback(
		(): EcosystemContentPanel[] =>
			filteredAvailableAddons.filter(isNewExtension).slice(0, SHOW_EXTENSION_COUNT_THRESHOLD),
		[filteredAvailableAddons],
	);

	const getExtensionsToShowInDropdown = useCallback(
		(): EcosystemContentPanel[] =>
			differenceBy(filteredAvailableAddons, getExtensionsToShowIconsFor(), 'moduleKey'),
		[filteredAvailableAddons, getExtensionsToShowIconsFor],
	);

	const sendQuickAddClickedEvent = useCallback(
		(
			isNewToIssue: boolean,
			name: string,
			appKey: string,
			moduleKey: string,
			extensionId: string,
			isForge: boolean,
			analyticEvent?: AnalyticsEvent,
		) => {
			if (analyticEvent) {
				const attributes = {
					addon_key: appKey,
					key: moduleKey,
					isNewToIssue: isNewToIssue.toString(),
				};

				fireUIAnalytics(analyticEvent, attributes);
			}
			// Should always call onQuickAddClick, because connect apps are relying on its event broadcasting
			onQuickAddClick(
				ISSUE_QUICK_ADD_CLICKED,
				{
					addon_key: appKey,
					key: moduleKey,
				},
				{ isNewToIssue: isNewToIssue.toString() },
			);

			if (isForge) {
				const issueViewAnalytics = createAnalyticsEvent({
					action: 'clicked',
					actionSubject: 'forgeApp',
				});
				const appName = name.replaceAll(/\s/g, '');
				const attributes: Attributes = {
					appName,
					appKey,
					moduleKey,
					actionType: 'forge',
				};
				fireUIAnalytics(issueViewAnalytics, attributes);

				// Forge UI analytics
				fireUiTriggerSelectedEvent(extensionId, source, {
					isNewToIssue,
				});
			}
		},
		[onQuickAddClick, source, createAnalyticsEvent],
	);

	const itemSelected = useCallback(
		(extension: EcosystemContentPanel | Panel, analyticEvent: AnalyticsEvent) => {
			const staticModuleKey = removeTimestamp(extension.moduleKey, 10);
			const isForge = extension.type === FORGE_ENTITY_TYPE;

			let extensionId = isForge
				? toForgeKey(extension.appKey, extension.moduleKey)
				: toConnectKey(extension.appKey, extension.moduleKey);

			if (isForge) {
				const prefix = `${extensionId}-`;
				const panelKey = contentPanelsInIssue.find((key) => isKeyMatchPrefix(key, prefix));

				// when allowMultiple is true we do not override extensionId to invoke onAddExtension prop
				// @ts-expect-error - TS2339 - Property 'allowMultiple' does not exist on type 'Panel | EcosystemContentPanel'.
				if (panelKey != null && extension.allowMultiple === false) {
					extensionId = panelKey;
				}
			}

			if (
				extension.showByDefault ||
				contentPanelsInIssue.includes(`${extension.appKey}_${staticModuleKey}`) ||
				contentPanelsInIssue.includes(extensionId)
			) {
				onContentPanelFocusAnalytics(
					contentPanelAnalyticsData(extension.appKey, extension.moduleKey),
				);
				onSelectContentPanel(extensionId);
				sendQuickAddClickedEvent(
					false,
					extension.name,
					extension.appKey,
					extension.moduleKey,
					extensionId,
					isForge,
					analyticEvent,
				);
			} else if (!(isForge && !canSaveForgeIssuePanel)) {
				onContentPanelAddAnalytics(
					contentPanelAnalyticsData(extension.appKey, extension.moduleKey),
				);
				onAddExtension(extensionId);
				sendQuickAddClickedEvent(
					true,
					extension.name,
					extension.appKey,
					extension.moduleKey,
					extensionId,
					isForge,
					analyticEvent,
				);
			}
		},
		[
			canSaveForgeIssuePanel,
			contentPanelsInIssue,
			onAddExtension,
			onContentPanelAddAnalytics,
			onContentPanelFocusAnalytics,
			onSelectContentPanel,
			sendQuickAddClickedEvent,
		],
	);

	// eslint does not understand the deboune
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const debouncedItemSelected = useCallback(debounce(itemSelected, 300), [itemSelected]);

	useEffect(
		() => () => {
			debouncedItemSelected.cancel();
		},
		[debouncedItemSelected],
	);

	const transformExtension = useCallback(
		(extension: EcosystemContentPanel) => {
			const dropDownItem = (
				<DropdownItemWithAnalytics
					key={extension.moduleKey}
					elemBefore={getIcon(extension)}
					// @ts-expect-error - TS7006 - Parameter '_' implicitly has an 'any' type.
					onClick={(_, analyticEvent: AnalyticsEvent) => itemSelected(extension, analyticEvent)}
				>
					{extension.name}
				</DropdownItemWithAnalytics>
			);
			return extension.tooltip ? (
				<AkToolTip content={extension.tooltip} key={extension.moduleKey}>
					{dropDownItem}
				</AkToolTip>
			) : (
				dropDownItem
			);
		},

		[getIcon, itemSelected],
	);

	const createDropdownItems = useCallback(
		(extensions: EcosystemContentPanel[]) => (
			<DropdownItemGroup>
				{extensions.map(transformExtension)}
				{canInstallAddons && (
					<DropdownItemWithAnalytics
						key={messages.addMore.id}
						elemBefore={
							<AddMoreTextWrapper>
								<AddIcon label={formatMessage(messages.addMore)} />
							</AddMoreTextWrapper>
						}
						onClick={(_: unknown, analyticEvent: AnalyticsEvent) =>
							onAddExtensionsClick(analyticEvent)
						}
						href={`${baseUrl}/plugins/servlet/upm/marketplace/featured`}
						target="_new"
					>
						<AddMoreTextWrapper>{formatMessage(messages.addMore)}</AddMoreTextWrapper>
					</DropdownItemWithAnalytics>
				)}
			</DropdownItemGroup>
		),

		[baseUrl, canInstallAddons, formatMessage, onAddExtensionsClick, transformExtension],
	);

	const transformExtensionToQuickIcon = useCallback(
		(extension: EcosystemContentPanel, currentIndex: number) =>
			createItemView({
				id: `${extension.appKey}_${extension.moduleKey}`,
				testId: `${ECOSYSTEM_CONTENT_BUTTON_BASE_DATA_TEST_ID}.${extension.appKey}_${extension.moduleKey}`,
				label: extension.tooltip || extension.name,
				icon: getIcon(extension),
				onClick: (_, analyticEvent: AnalyticsEvent) =>
					debouncedItemSelected(extension, analyticEvent),
				itemIndex: startIndex + currentIndex,
			}),
		[createItemView, getIcon, debouncedItemSelected, startIndex],
	);

	const extensionsToShow = useMemo((): ItemViewType[] => {
		const extensionsToShowinLocal = getExtensionsToShowIconsFor();
		const addonQuickAddItems = extensionsToShowinLocal.map((addon, currentIndex) =>
			transformExtensionToQuickIcon(addon, currentIndex),
		);
		return addonQuickAddItems;
	}, [getExtensionsToShowIconsFor, transformExtensionToQuickIcon]);

	const extensionDropdownItems = useMemo(() => {
		const dropdownExtensionItems = getExtensionsToShowInDropdown();
		const shouldRenderDropdown = dropdownExtensionItems.length > 0 || canInstallAddons;
		const eventsProps = {
			onClick: (_: unknown, analyticEvent: AnalyticsEvent) =>
				onAddExtensionsDropdownTriggerClick(analyticEvent),
		};

		return shouldRenderDropdown ? (
			<DropdownMenu
				key="ecosystem-actions-dropdown"
				// @ts-expect-error - TS2769 - No overload matches this call.
				boundariesElement="scrollParent"
				testId="issue-view-ecosystem.dropdown-menu"
				shouldRenderToParent
				trigger={({ triggerRef, ...triggerProps }) => (
					<CustomThemeButton
						{...triggerProps}
						spacing="none"
						iconBefore={
							<IconWrapper>
								<ShowMoreHorizontalIcon LEGACY_size="medium" label="" spacing="spacious" />
							</IconWrapper>
						}
						theme={(current, props) => {
							const styles = current(props);

							return {
								...styles,
								buttonStyles: {
									...styles.buttonStyles,
									margin: 0,
								},
							};
						}}
						aria-label={formatMessage(messages.quickAddAddonLabel)}
						{...eventsProps}
						onClick={(event, analyticEvent) => {
							triggerProps?.onClick?.(event);
							eventsProps.onClick(event, analyticEvent);
						}}
						ref={triggerRef}
						interactionName="auto-generated.jira-issue-view-ecosystem.button"
					/>
				)}
			>
				{createDropdownItems(dropdownExtensionItems)}
			</DropdownMenu>
		) : null;
	}, [
		canInstallAddons,
		createDropdownItems,
		formatMessage,
		getExtensionsToShowInDropdown,
		onAddExtensionsDropdownTriggerClick,
	]);

	/*
	 *  Simply returning the list has the same effect as the parent styled-component
	 *  already provides the inline-flex prop, but this is required as a workaround
	 *  to enzyme not yet supporting rendered lists
	 */

	if (isQuickActionsListView && fg('quick_actions_menu_ga')) {
		if (fg('concurrent-rendering-fix-unique-list-key')) {
			return <>{createDropdownItems(filteredAvailableAddons)}</>;
		}

		return <>{[createDropdownItems(filteredAvailableAddons)]}</>;
	}

	return (
		<EcosystemActionsWrapper data-testid="issue-view-ecosystem.ecosystem-actions-wrapper">
			{[...extensionsToShow, extensionDropdownItems]}
		</EcosystemActionsWrapper>
	);
};

/**
 * Export for testing only, don't use this function out of this file
 */
export const onQuickAddClick = (
	eventName: string,
	addonFilter: AddonFilter,
	eventData: EventData,
) => {
	broadcastConnectEvent(eventName, addonFilter, eventData);
};

const UnsafeAddonsQuickAddWithCondition = UnsafeAddonsQuickAdd;

const UnsafeAddonsQuickAddWithConnect = flow(
	withFireUiAnalytics({
		onAddExtensionsClick: 'quickAddDropdownMenuItem',
		onAddWebLinkClick: 'quickAddWebLink',
		onAddExtensionsDropdownTriggerClick: 'quickAddDropdownMenuTrigger',
	}),
	connect(
		(state) => ({
			availableAddons: ecosystemAllPanelsSelector(state),
			contentPanelsInIssue: issueContentPanelsSelector(state),
			baseUrl: baseUrlSelector(state),
			canInstallAddons: canInstallAddonsSelector(state),
			canSaveForgeIssuePanel: forgeUserCanSaveIssuePanelSelector(state),
			source: analyticsSourceSelector(state),
		}),
		(dispatch) => ({
			onAddWebLinkClick: () => dispatch(showCreateWebLinkForm()),
			onQuickAddClick,
		}),
	),
	safeComponent(),
)(UnsafeAddonsQuickAddWithCondition);

export default UnsafeAddonsQuickAddWithConnect;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const IconWrapper = styled.span({
	// TODO Delete this comment after verifying space token -> previous value ``${gridSize / 2}px``
	paddingTop: token('space.050', '4px'),
	paddingRight: token('space.050', '4px'),
	paddingBottom: token('space.050', '4px'),
	paddingLeft: token('space.050', '4px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const EcosystemActionsWrapper = styled.div({
	display: 'inline-flex',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const AddMoreTextWrapper = styled.span({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.icon.brand', colors.B400),
});
